import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";
import { useMutation } from "@tanstack/react-query";
import {
  AllCommunityModule,
  CellSelectionOptions,
  ColDef,
  GetRowIdParams,
  ModuleRegistry,
  RowSelectionOptions,
  Theme,
  themeQuartz
} from "ag-grid-community";
import { AgGridReact, CustomCellRendererProps } from "ag-grid-react";
import { useSearchParams } from "react-router-dom";
import { Stack, LinearProgress, Typography, Button } from "@mui/material";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import { fetchGraphQL } from "../../Graphql/utils";
import AlertBannerContext from "../../Contexts/alertBannerContext";
import WithAuth from "../../Hoc/WithAuth";
import { getAllServiceCategoriesQuery } from "../../Graphql/querries/serviceCategoryQuery";
import {
  getAllServiceCategoriesResponse,
  ServiceCategory
} from "../../Graphql/types/serviceCategory";
import ServiceDetailDialog from "./ServiceDetailDialog";

ModuleRegistry.registerModules([AllCommunityModule]);

const Services = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const [rowData, setRowData] = useState<ServiceCategory[]>([]);
  const [columnDefs, setColumnDefs] = useState<ColDef[]>();
  const [searchParams] = useSearchParams();
  const [openServiceDialog, setOpenServiceDialog] = useState(false);
  const [openAddServiceDialog, setOpenAddServiceDialog] = useState(false);
  const [selectedService, setSelectedService] = useState<
    ServiceCategory | undefined
  >(undefined);

  const containerStyle = useMemo(
    () => ({ width: "100%", height: "calc(100vh - 100px)" }),
    []
  );
  const gridStyle = useMemo(() => ({ height: "100%", width: "100%" }), []);
  const myTheme = themeQuartz.withParams({
    // color and style of border around selection
    rangeSelectionBorderColor: "rgb(193, 0, 97)",
    rangeSelectionBorderStyle: "dashed",
    // background color of selection - you can use a semi-transparent color
    // and it wil overlay on top of the existing cells
    rangeSelectionBackgroundColor: "rgb(255, 0, 128, 0.1)",
    // color used to indicate that data has been copied form the cell range
    rangeSelectionHighlightColor: "rgb(60, 188, 0, 0.3)"
  });

  const theme = useMemo<Theme | "legacy">(() => {
    return myTheme;
  }, []);

  const { mutate, isPending } = useMutation<
    getAllServiceCategoriesResponse,
    Error
  >({
    mutationKey: ["getServiceList"],
    mutationFn: () => fetchGraphQL(getAllServiceCategoriesQuery, {}, setAlert),
    onSuccess: (response) => {
      setRowData(response.getAllServiceCategories || []);
    },
    onError: () => {
      setRowData([]);
    }
  });

  const rowSelection: RowSelectionOptions = {
    mode: "singleRow",
    checkboxes: false
  };

  const cellSelection = useMemo<boolean | CellSelectionOptions>(() => {
    return { handle: { mode: "range" } };
  }, []);

  /* eslint-disable-next-line react/no-unstable-nested-components */
  const OpenServiceDetailButton = ({ data }: CustomCellRendererProps) => (
    <Button
      onClick={() => {
        setSelectedService(data);
        setOpenServiceDialog(true);
      }}
      variant="text"
      startIcon={<RemoveRedEyeIcon />}
    >
      View more...
    </Button>
  );

  useEffect(() => {
    setColumnDefs([
      { field: "id", headerName: "id" },
      { field: "title", headerName: "Title" },
      { field: "description", headerName: "Descriptions", minWidth: 250 },
      {
        field: "actions",
        headerName: "Actions",
        cellRenderer: OpenServiceDetailButton
      }
    ]);
  }, []);

  useEffect(() => {
    mutate();
  }, [searchParams]);

  const getRowId = useCallback(
    (params: GetRowIdParams) => String(params.data.id),
    []
  );

  const defaultColDef = useMemo(() => {
    return {
      filter: true,
      floatingFilter: true,
      flex: 1,
      minWidth: 100,
      resizable: true
    };
  }, []);

  return (
    <div style={containerStyle}>
      {isPending && (
        <Stack width="100%" paddingBottom="1em" alignSelf="center">
          <LinearProgress />
        </Stack>
      )}
      <Stack
        style={{
          display: "flex",
          justifyContent: "center",
          flexDirection: "row",
          width: "100%",
          padding: "1em"
        }}
      >
        <Typography variant="h6" fontWeight="bold">
          Service Listing
        </Typography>
      </Stack>
      <Stack
        style={{
          display: "flex",
          justifyContent: "right",
          flexDirection: "row",
          width: "100%",
          padding: "2em",
          boxSizing: "border-box"
        }}
      >
        <Button
          variant="contained"
          onClick={() => {
            setOpenServiceDialog(true);
          }}
        >
          Add a new Service
        </Button>
      </Stack>
      <div style={gridStyle}>
        <AgGridReact
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          rowSelection={rowSelection}
          pagination
          paginationPageSize={10}
          paginationPageSizeSelector={[10, 25, 50]}
          theme={theme}
          cellSelection={cellSelection}
          getRowId={getRowId}
        />
      </div>
      {openServiceDialog && (
        <ServiceDetailDialog
          open={openServiceDialog}
          onClose={() => {
            setOpenServiceDialog(false);
            setSelectedService(undefined);
            mutate();
          }}
          service={selectedService || ({} as ServiceCategory)}
        />
      )}

      {openAddServiceDialog && (
        <ServiceDetailDialog
          open={openAddServiceDialog}
          onClose={() => {
            setOpenAddServiceDialog(false);
            setSelectedService(undefined);
            mutate();
          }}
          service={{} as ServiceCategory}
        />
      )}
    </div>
  );
};

export default WithAuth(Services);
